import InfiniteScroll from 'react-infinite-scroll-component';
import { useCallback, useContext, useMemo } from 'react';
import { IssuerAddUpdateBankModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateBankModal.tsx';
import {
  useIssuerDeleteBankMutation,
  useRegisterBanksListLazyQuery,
} from '@/modules/issuer-dashboard/queries/banks.ts';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon } from 'ui/icons';
import { fixNameEnum, goodFormatNumber, goodFormatNumberCustom } from 'ui/utils';
import { RegisterBankExtended } from 'api';

type BankTable =
  | 'Bank account'
  | 'Bank type'
  | 'Available cash'
  | 'Currency'
  | 'Portion of portfolio value'
  | '% of register value'
  | '';

const tableHeadItems: TableHeadColumn<BankTable>[] = [
  { title: 'Bank account' },
  { title: 'Bank type' },
  { title: 'Currency' },
  { title: 'Available cash' },
  { title: 'Portion of portfolio value' },
  { title: '% of register value' },
  { title: '' },
];

export const PortfolioBankAccountsTab = () => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataBanksList,
  } = useRegisterBanksListLazyQuery({
    page: 1,
    page_size: 15,
  });

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataBanksList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataBanksList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(item => {
          const {
            bank_account_name,
            bank_types,
            total_available_cash,
            portion_of_portfolio_value,
            percent_of_portfolio_value,
            bank_currency,
          } = item;

          const currency = bank_currency.toUpperCase();

          const body: BodyCell[] = [
            {
              cell: <div className="text-blue-700">{bank_account_name}</div>,
            },
            {
              cell: <span>{bank_types?.map(item => fixNameEnum(item)).join(', ')}</span>,
            },
            { cell: currency?.toUpperCase() },
            {
              cell: (
                <span>{goodFormatNumberCustom(total_available_cash || 0, currency, 0, 2)}</span>
              ),
            },
            {
              cell: (
                <span>
                  {goodFormatNumberCustom(portion_of_portfolio_value || 0, currency, 0, 2)}
                </span>
              ),
            },
            {
              cell: <span>{goodFormatNumber(percent_of_portfolio_value || 0)}%</span>,
            },
            { cell: <BankMoreButton item={item} /> },
          ];

          return {
            cells: body,
          };
        }),
      );
    });
    return result;
  }, [dataBanksList]);

  const handleOpenAddBankModal = useCallback(() => {
    openModal(IssuerAddUpdateBankModal);
  }, [openModal]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full"></div>

        <div className="flex gap-2">
          <Button className="w-[144px] px-0" onClick={handleOpenAddBankModal}>
            Add bank account
          </Button>
        </div>
      </div>
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={dataBanksList?.pages.length ?? 0}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex h-16 w-full items-center justify-center">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col">
          <Table
            className="my-0"
            classNameHead="border-t-0"
            withRounded={isMobile}
            withBorder={true}
            withBorderX={false}
            headItems={tableHeadItems}
            items={tableItems}
            isLoading={isLoading}
            isError={isError}
          />
        </div>
      </InfiniteScroll>
    </div>
  );
};

export interface BankMoreButtonProps {
  item: RegisterBankExtended;
}

export const BankMoreButton = ({ item }: BankMoreButtonProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeleteBankMutation();
  const items = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal(IssuerAddUpdateBankModal, { item });
        },
      },
      {
        label: 'Archive',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Archive</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal]);

  return (
    <div>
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        autoPlace
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
